import type { ReactNode } from 'react'

import { cn } from '../utilitarios/cn'

export type TomEtiqueta =
  | 'neutro'
  | 'marca'
  | 'sucesso'
  | 'atencao'
  | 'perigo'
  | 'info'
  | 'frio'
  | 'quente'
  | 'abordagem'
  | 'em-contato'

const TONS: Record<TomEtiqueta, string> = {
  neutro: 'bg-superficie-3 text-conteudo-suave ring-borda-forte/60',
  marca: 'bg-marca-50 text-marca-700 ring-marca-200',
  sucesso: 'bg-sucesso-suave text-sucesso ring-sucesso/30',
  atencao: 'bg-atencao-suave text-atencao ring-atencao/30',
  perigo: 'bg-perigo-suave text-perigo ring-perigo/30',
  info: 'bg-info-suave text-info ring-info/30',
  frio: 'bg-frio/10 text-frio ring-frio/25',
  quente: 'bg-quente/10 text-quente ring-quente/25',
  abordagem: 'bg-abordagem/10 text-abordagem ring-abordagem/25',
  'em-contato': 'bg-em-contato/10 text-em-contato ring-em-contato/25',
}

export interface PropriedadesEtiqueta {
  tom?: TomEtiqueta
  tamanho?: 'sm' | 'md'
  /** Ponto colorido à esquerda — usado quando a cor vem do cadastro (motivo). */
  cor?: string | null
  children: ReactNode
  className?: string
  title?: string
}

/**
 * Badge de status. `ring` em vez de `border` para não alterar a altura da linha
 * da tabela quando a etiqueta entra e sai (o filtro "Ocultar finalizados" troca a
 * coluna Status inteira, e a tabela pularia).
 */
export function Etiqueta({
  tom = 'neutro',
  tamanho = 'sm',
  cor,
  children,
  className,
  title,
}: PropriedadesEtiqueta) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-full font-semibold uppercase tracking-wide ring-1 ring-inset',
        tamanho === 'sm' ? 'px-2 py-0.5 text-[0.6875rem]' : 'px-2.5 py-1 text-xs',
        TONS[tom],
        className,
      )}
    >
      {cor ? (
        <span
          aria-hidden="true"
          className="h-2 w-2 shrink-0 rounded-full ring-1 ring-black/10"
          style={{ backgroundColor: cor }}
        />
      ) : null}
      <span className="truncate">{children}</span>
    </span>
  )
}

/** Contador ao lado de um rótulo (cabeçalho de coluna do painel, aba). */
export function Contador({
  valor,
  tom = 'neutro',
  className,
}: {
  valor: number
  tom?: TomEtiqueta
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums ring-1 ring-inset',
        TONS[tom],
        className,
      )}
    >
      {valor}
    </span>
  )
}
